/**
 * The account, at the foot of the left rail.
 *
 * Down here rather than in the topbar because the topbar is where the things
 * you touch while reading a chart live -- symbol, timeframe, bar count -- and
 * an account you open twice a week does not belong among them.
 *
 * It opens a menu rather than acting on the click. The avatar used to sign
 * out immediately, so the only control that ends a session was a single
 * unconfirmed click next to the full-screen button.
 */
import { useEffect, useRef, useState } from "react";

import type { AuthUser } from "../lib/api";

export function ProfileButton({
  user, onSignOut, onAdmin, onResearch,
  extendedHours, onExtendedHours,
  theme, onTheme,
}: {
  user: AuthUser;
  onSignOut: () => void | Promise<void>;
  /** Shown only to an operator; the engine enforces the same rule. */
  onAdmin?: () => void;
  onResearch?: () => void;
  /** When provided, a toggle appears in the menu for pre- / post-market data. */
  extendedHours?: boolean;
  onExtendedHours?: (on: boolean) => void;
  /** When provided, a light/dark toggle appears in the menu. */
  theme?: "light" | "dark";
  onTheme?: (next: "light" | "dark") => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const name = (user.name ?? "").trim() || user.email;
  const initial = name.charAt(0).toUpperCase();

  return (
    <div className="profile" ref={wrap}>
      <button
        className={`profile-avatar ${open ? "on" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name}
      >
        {/* The initial, not a glyph: there is no photo to show, and a generic
            person icon says less than the letter does. */}
        {initial}
      </button>

      {open && (
        <div className="profile-menu" role="menu">
          <div className="pm-who">
            <div className="pm-name">{name}</div>
            {/* The address is the thing that tells two accounts apart, so it
                is shown even when a name exists. */}
            <div className="pm-email">{user.email}</div>
          </div>
          <div className="pm-sep" />
          {onExtendedHours && (
            <label
              className="pm-item pm-toggle"
              role="menuitemcheckbox"
              aria-checked={!!extendedHours}
              title="Include pre-market (04:00) and after-hours (20:00) data on all intraday charts."
            >
              <span>Extended hours</span>
              <input
                type="checkbox"
                checked={!!extendedHours}
                onChange={(e) => onExtendedHours(e.target.checked)}
              />
            </label>
          )}
          {onTheme && (
            <label
              className="pm-item pm-toggle"
              role="menuitemcheckbox"
              aria-checked={theme === "light"}
              title="Switch between the dark trader palette and a TradingView-style light theme."
            >
              <span>Light theme</span>
              <input
                type="checkbox"
                checked={theme === "light"}
                onChange={(e) => onTheme(e.target.checked ? "light" : "dark")}
              />
            </label>
          )}
          {onResearch && (
            <button
              className="pm-item"
              role="menuitem"
              onClick={() => { onResearch(); setOpen(false); }}
            >
              Options research
            </button>
          )}
          {user.is_admin && onAdmin && (
            <button
              className="pm-item"
              role="menuitem"
              onClick={() => { onAdmin(); setOpen(false); }}
            >
              Administration
            </button>
          )}
          <button
            className="pm-item danger"
            role="menuitem"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onSignOut();
              } finally {
                setBusy(false);
                setOpen(false);
              }
            }}
          >
            {busy ? "Signing out…" : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}
